import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Change, Loader, Empty, RetryState } from "@/components/widgets";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MagnifyingGlass, ListDashes, CaretUp, CaretDown, Stack, Rows } from "@phosphor-icons/react";
import { AssetLogo } from "@/components/AssetLogo";

const GRID = "grid-cols-[100px_1fr_170px_110px_110px]";

export default function Stocks() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [sector, setSector] = useState("any");
  const [grouped, setGrouped] = useState(true);
  const [sort, setSort] = useState({ col: "change_pct", dir: "desc" });
  const [err, setErr] = useState(false);

  const load = () => {
    setErr(false);
    api.get("/stocks").then((r) => setData(r.data)).catch(() => setErr(true));
  };
  useEffect(() => { load(); }, []);

  // Cold pod: prices "warming up" — poll until they arrive instead of hard-failing.
  useEffect(() => {
    if (!data?.warming) return;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [data?.warming]);

  const setSortCol = (col) => {
    setSort((s) => s.col === col
      ? { col, dir: s.dir === "asc" ? "desc" : "asc" }
      : { col, dir: col === "ticker" || col === "name" ? "asc" : "desc" });
  };

  const sortRows = useMemo(() => (rows) => {
    const { col, dir } = sort;
    const s = [...rows];
    s.sort((a, b) => {
      let av = a[col], bv = b[col];
      if (col === "ticker" || col === "name" || col === "sector") {
        return dir === "asc" ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
      }
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return dir === "asc" ? av - bv : bv - av;
    });
    return s;
  }, [sort]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const term = q.trim().toUpperCase();
    return data.stocks.filter((s) =>
      (sector === "any" || s.sector === sector) &&
      (!term || s.ticker.includes(term) || s.name.toUpperCase().includes(term))
    );
  }, [data, q, sector]);

  const groups = useMemo(() => {
    if (!grouped) return null;
    const map = {};
    filtered.forEach((s) => { (map[s.sector] = map[s.sector] || []).push(s); });
    return Object.entries(map)
      .map(([name, rows]) => {
        const vals = rows.filter((r) => r.change_pct != null).map((r) => r.change_pct);
        const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
        return { name, rows: sortRows(rows), avg: Number(avg.toFixed(2)) };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [filtered, grouped, sortRows]);

  if (!data) return err
    ? <RetryState label="Couldn't load stocks — the market data feed may be busy." onRetry={load} />
    : <Loader label="Loading stock universe" />;

  return (
    <div>
      <div className="flex items-end justify-between">
        <div>
          <div className="label-xs">Market Data Layer</div>
          <h1 className="mt-1 font-heading text-3xl font-bold tracking-tighter flex items-center gap-2"><ListDashes size={26} className="text-volt" /> Stocks</h1>
        </div>
        <span className="mono text-xs text-muted-foreground">{data.count} symbols · {data.sectors.length} sectors</span>
      </div>

      {data.warming && (
        <div data-testid="stocks-warming" className="mt-4 flex items-center gap-2 border border-volt/30 bg-volt/[0.06] rounded-sm px-3 py-2 text-xs text-muted-foreground">
          <span className="h-2 w-2 rounded-full bg-volt animate-pulse" />
          Live prices are warming up from the market feed — refreshing automatically…
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="stocks-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ticker or company" className="pl-9 bg-card rounded-sm h-9" />
        </div>
        <Select value={sector} onValueChange={setSector}>
          <SelectTrigger data-testid="stocks-sector" className="bg-card rounded-sm h-9 w-[190px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">All sectors</SelectItem>
            {data.sectors.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <button data-testid="toggle-group" onClick={() => setGrouped((g) => !g)}
          className="flex items-center gap-2 border border-border bg-card rounded-sm h-9 px-3 text-sm text-muted-foreground hover:text-white transition-colors duration-200">
          {grouped ? <Stack size={16} className="text-volt" /> : <Rows size={16} />}
          {grouped ? "Grouped by Sector" : "Flat List"}
        </button>
        <span className="mono text-xs text-muted-foreground">{filtered.length} shown</span>
      </div>

      <div className="mt-4 border border-border bg-card overflow-hidden">
        <div className={`grid ${GRID} gap-2 border-b border-border px-4 py-2.5 label-xs`}>
          <SortHead label="Symbol" col="ticker" sort={sort} onClick={setSortCol} />
          <SortHead label="Company" col="name" sort={sort} onClick={setSortCol} />
          <span>Sector</span>
          <SortHead label="Price" col="price" sort={sort} onClick={setSortCol} align="right" />
          <SortHead label="Change" col="change_pct" sort={sort} onClick={setSortCol} align="right" />
        </div>

        {filtered.length === 0 ? <Empty>No matching stocks</Empty> : grouped ? (
          groups.map((g) => (
            <div key={g.name}>
              <div className="flex items-center justify-between bg-white/[0.03] border-b border-border px-4 py-2" data-testid={`sector-group-${g.name.replace(/ /g, "-")}`}>
                <span className="label-xs text-white">{g.name} <span className="text-muted-foreground">· {g.rows.length}</span></span>
                <span className="mono text-xs">avg <Change value={g.avg} className="text-xs" /></span>
              </div>
              {g.rows.map((s) => <StockRow key={s.ticker} s={s} onClick={() => nav(`/stock/${s.ticker}`)} />)}
            </div>
          ))
        ) : (
          sortRows(filtered).map((s) => <StockRow key={s.ticker} s={s} onClick={() => nav(`/stock/${s.ticker}`)} />)
        )}
      </div>
    </div>
  );
}

function SortHead({ label, col, sort, onClick, align = "left" }) {
  const active = sort.col === col;
  return (
    <button data-testid={`sort-${col}`} onClick={() => onClick(col)}
      className={`flex items-center gap-1 hover:text-white transition-colors duration-200 ${align === "right" ? "justify-end" : ""} ${active ? "text-volt" : ""}`}>
      {label}
      {active && (sort.dir === "asc" ? <CaretUp size={11} weight="bold" /> : <CaretDown size={11} weight="bold" />)}
    </button>
  );
}

function StockRow({ s, onClick }) {
  return (
    <button data-testid={`stock-row-${s.ticker}`} onClick={onClick}
      className={`grid w-full ${GRID} gap-2 items-center px-4 py-3 border-b border-border/50 hover:bg-white/[0.03] transition-colors duration-200 text-left`}>
      <span className="mono font-medium flex items-center gap-2"><AssetLogo ticker={s.ticker} size={22} />{s.ticker}</span>
      <span className="text-sm truncate">{s.name}</span>
      <span className="text-xs text-muted-foreground truncate">{s.sector}</span>
      <span className="mono text-right text-sm">{s.price != null ? `$${s.price}` : "—"}</span>
      <span className="text-right">{s.change_pct != null ? <Change value={s.change_pct} className="text-xs" /> : <span className="text-muted-foreground text-xs">—</span>}</span>
    </button>
  );
}
